/**
 * Series response → chart series builders for check-ins, active members
 * (WSRADM-0037), and revenue-by-family (WSRADM-0038 / WSRADM-0040). Never sum
 * checkinBands or families — render counts as sent. Hidden series (WSRADM-0051)
 * are dropped from the drawn series but kept in the legend, marked hidden.
 */
import {
  DASHBOARD_BAND_PALETTE,
  DASHBOARD_OTHER_BAND_COLOR,
  DASHBOARD_UNKNOWN_BAND_COLOR,
  FAMILY_PALETTE,
  familyColorOrNeutral,
} from '@/components/familyPalette';
import type { CheckinHourCount, SeriesFamilyCell, SeriesResponse } from '@/api/reports/types';
import { bandViewKey, familyViewKey } from '@/config/viewPrefs';
import { shiftOneYearBack } from './rangeModel';

const NONE_HIDDEN: ReadonlySet<string> = new Set();

/** One clickable legend entry; `key` is the view (hide) key. */
export interface LegendItem {
  key: string;
  label: string;
  color: string;
  hidden: boolean;
}

/** Neutral grey when family colour is null / invalid (Unassigned). */
export const NEUTRAL_FAMILY_COLOR = '#9E9E9E';

export const OTHER_BAND_LABEL = 'Other';
export const UNKNOWN_BAND_LABEL = 'Unknown time';

export interface BandSeries {
  key: string;
  label: string;
  color: string;
  data: number[];
  synthetic: boolean;
}

function countForLabel(
  bands: { label: string; count: number }[],
  label: string,
): number {
  for (const cell of bands) {
    if (cell.label === label) return cell.count;
  }
  return 0;
}

function allZeros(data: number[]): boolean {
  for (const n of data) {
    if (n !== 0) return false;
  }
  return true;
}

/**
 * One stacked series per attendance band (+ Other / Unknown time). `series`
 * is what is drawn (hidden dropped); `legend` lists every series.
 */
export function checkinSeries(
  r: SeriesResponse,
  hidden: ReadonlySet<string> = NONE_HIDDEN,
): { periods: string[]; series: BandSeries[]; legend: LegendItem[] } {
  const periods = r.buckets.map((b) => b.period);
  const series: BandSeries[] = [];

  r.attendanceBands.forEach((band, index) => {
    const color = DASHBOARD_BAND_PALETTE[index % DASHBOARD_BAND_PALETTE.length]!;
    const data = r.buckets.map((bucket) => countForLabel(bucket.checkinBands, band.label));
    series.push({
      key: band.label,
      label: band.label,
      color,
      data,
      synthetic: false,
    });
  });

  const otherData = r.buckets.map((bucket) => countForLabel(bucket.checkinBands, OTHER_BAND_LABEL));
  const otherSeries: BandSeries = {
    key: OTHER_BAND_LABEL,
    label: OTHER_BAND_LABEL,
    color: DASHBOARD_OTHER_BAND_COLOR,
    data: otherData,
    synthetic: true,
  };
  if (!allZeros(otherData)) series.push(otherSeries);

  const unknownData = r.buckets.map((bucket) =>
    countForLabel(bucket.checkinBands, UNKNOWN_BAND_LABEL),
  );
  const unknownSeries: BandSeries = {
    key: UNKNOWN_BAND_LABEL,
    label: UNKNOWN_BAND_LABEL,
    color: DASHBOARD_UNKNOWN_BAND_COLOR,
    data: unknownData,
    synthetic: true,
  };
  if (!allZeros(unknownData)) series.push(unknownSeries);

  const legend = series.map((s) => ({
    key: bandViewKey(s.key),
    label: s.label,
    color: s.color,
    hidden: hidden.has(bandViewKey(s.key)),
  }));

  return {
    periods,
    series: series.filter((s) => !hidden.has(bandViewKey(s.key))),
    legend,
  };
}

/** Active-members bars; axis label key from server semantic. */
export function activeMembersSeries(r: SeriesResponse): {
  periods: string[];
  data: number[];
  axisLabelKey: 'coveredOnDay' | 'coveredInMonth';
} {
  return {
    periods: r.buckets.map((b) => b.period),
    data: r.buckets.map((b) => b.activeMembers),
    axisLabelKey:
      r.activeMembersSemantic === 'COVERED_ON_DAY' ? 'coveredOnDay' : 'coveredInMonth',
  };
}

export interface RevenueSeriesSpec {
  periods: string[];
  families: { key: string; label: string; color: string; data: number[]; stack: 'families' }[];
  total: { key: 'total'; label: string; color: string; data: number[] };
  legend: LegendItem[];
  overlapBuckets: boolean[];
}

function columnSeriesKey(slug: string | null): string {
  return slug ?? '__unmapped__';
}

function familyRevenueAt(families: SeriesFamilyCell[], slug: string | null): number {
  for (const cell of families) {
    if (cell.slug === slug) return cell.revenue;
  }
  return 0;
}

/**
 * Resolve a family column colour: tenant colour when valid; palette-by-index among
 * null-coloured non-Unassigned columns; Unassigned (slug null) always neutral grey.
 */
function familyColumnColor(
  column: { slug: string | null; color: string | null },
  nullColourIndex: { i: number },
): string {
  if (column.slug === null) return NEUTRAL_FAMILY_COLOR;
  const resolved = familyColorOrNeutral(column.color);
  if (resolved !== null) return resolved.bg;
  const color = FAMILY_PALETTE[nullColourIndex.i % FAMILY_PALETTE.length]!;
  nullColourIndex.i += 1;
  return color;
}

/**
 * One colour per family column, in order — same rule as the stacked bars
 * (for legend / tooltip chips).
 */
export function resolveFamilyColumnColors(
  columns: readonly { slug: string | null; color: string | null }[],
): string[] {
  const nullColourIndex = { i: 0 };
  return columns.map((column) => familyColumnColor(column, nullColourIndex));
}

/**
 * Always-stacked family bars + Total data for the tooltip (buckets[].revenue
 * as sent). Never sums family revenues; never compares a stack to revenue.
 * `totalColor` is theme.palette.text.primary — model stays theme-free.
 * Legend is families only when a dimension is set; empty when dimension is null
 * (the chart renders a single Total bar and hides the legend). Hidden families
 * are dropped from `families` (drawn) and kept in `legend`; `total` is
 * unchanged by hiding.
 */
export function revenueSeries(
  r: SeriesResponse,
  totalColor: string,
  totalLabel: string,
  hidden: ReadonlySet<string> = NONE_HIDDEN,
): RevenueSeriesSpec {
  const periods = r.buckets.map((b) => b.period);
  const overlapBuckets = r.buckets.map((b) => b.overlapLineCount > 0);
  const total = {
    key: 'total' as const,
    label: totalLabel,
    color: totalColor,
    data: r.buckets.map((b) => b.revenue),
  };

  if (r.dimension === null) {
    return {
      periods,
      families: [],
      total,
      legend: [],
      overlapBuckets,
    };
  }

  const colours = resolveFamilyColumnColors(r.familyColumns);
  const all = r.familyColumns.map((column, index) => ({
    viewKey: familyViewKey(column.slug),
    key: columnSeriesKey(column.slug),
    label: column.displayName,
    color: colours[index]!,
    data: r.buckets.map((bucket) => familyRevenueAt(bucket.families, column.slug)),
    stack: 'families' as const,
  }));

  const legend = all.map((f) => ({
    key: f.viewKey,
    label: f.label,
    color: f.color,
    hidden: hidden.has(f.viewKey),
  }));
  const families = all
    .filter((f) => !hidden.has(f.viewKey))
    .map(({ key, label, color, data, stack }) => ({ key, label, color, data, stack }));

  return { periods, families, total, legend, overlapBuckets };
}

export interface YoyAlignedSeries {
  revenue: (number | null)[];
  activeMembers: (number | null)[];
  checkins: (number | null)[];
  periods: (string | null)[];
}

/**
 * Align last year's series to current-period buckets.
 * - Month granularity: bucket i <-> bucket i
 * - Day granularity: bucket whose date is current date minus 1 year (never interpolate)
 */
export function alignYoySeries(
  current: SeriesResponse,
  lastYear: SeriesResponse,
): YoyAlignedSeries {
  const isMonth = (current.granularity ?? 'month') === 'month';
  const revenue: (number | null)[] = [];
  const activeMembers: (number | null)[] = [];
  const checkins: (number | null)[] = [];
  const periods: (string | null)[] = [];

  const lastYearByPeriod = new Map(lastYear.buckets.map((b) => [b.period, b]));

  for (let i = 0; i < current.buckets.length; i++) {
    const currentBucket = current.buckets[i]!;
    let lyBucket: typeof currentBucket | undefined;

    if (isMonth) {
      lyBucket = lastYear.buckets[i];
    } else {
      const targetDate = shiftOneYearBack(currentBucket.period);
      lyBucket = lastYearByPeriod.get(targetDate);
    }

    if (lyBucket) {
      revenue.push(lyBucket.revenue);
      activeMembers.push(lyBucket.activeMembers);
      checkins.push(lyBucket.checkins);
      periods.push(lyBucket.period);
    } else {
      revenue.push(null);
      activeMembers.push(null);
      checkins.push(null);
      periods.push(null);
    }
  }

  return { revenue, activeMembers, checkins, periods };
}

/**
 * Normalise checkinsByHourTotal to 25 items: hours 0..23 followed by -1 (No time).
 * Returns empty array if raw is undefined or empty.
 */
export function normalizeCheckinsByHour(
  raw: readonly CheckinHourCount[] | undefined,
): CheckinHourCount[] {
  if (!raw || raw.length === 0) return [];
  const map = new Map(raw.map((item) => [item.hour, item.count]));
  const result: CheckinHourCount[] = [];
  for (let h = 0; h < 24; h++) {
    result.push({ hour: h, count: map.get(h) ?? 0 });
  }
  result.push({ hour: -1, count: map.get(-1) ?? 0 });
  return result;
}

/**
 * Align last year's hourly counts with current-period normalized hours.
 */
export function alignYoyHourlyCheckins(
  currentHours: readonly CheckinHourCount[],
  lastYearHours: readonly CheckinHourCount[] | undefined,
): (number | null)[] {
  if (!lastYearHours || lastYearHours.length === 0) {
    return currentHours.map(() => null);
  }
  const lyMap = new Map(lastYearHours.map((h) => [h.hour, h.count]));
  return currentHours.map((h) => lyMap.get(h.hour) ?? null);
}
